import { useState, useEffect } from "react";
import { api } from "@/App";
import { toast } from "sonner";
import {
  TrendingUp,
  DollarSign,
  Users,
  FileText,
  FileSignature,
  Share2,
  RefreshCw,
  Calendar,
  ArrowUpRight,
  ArrowDownRight,
  Minus,
  Lock,
  Download,
  BarChart3,
  PieChart
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const StatCard = ({ icon: Icon, label, value, subtext, trend, trendValue, color }) => (
  <Card className="bg-slate-800/50 border-slate-700/50">
    <CardContent className="pt-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-indigo-400">{label}</p>
          <p className="text-2xl font-bold text-indigo-500">{value}</p>
          {subtext && <p className="text-xs text-slate-400 mt-1">{subtext}</p>}
          {trend && (
            <div className={`flex items-center gap-1 mt-1 text-xs ${
              trend === 'up' ? 'text-green-400' : trend === 'down' ? 'text-red-400' : 'text-indigo-400'
            }`}>
              {trend === 'up' ? <ArrowUpRight className="w-3 h-3" /> : 
               trend === 'down' ? <ArrowDownRight className="w-3 h-3" /> : 
               <Minus className="w-3 h-3" />}
              <span>{trendValue}</span>
            </div>
          )}
        </div>
        <div className={`p-3 rounded-xl ${color}`}>
          <Icon className="w-6 h-6" />
        </div>
      </div>
    </CardContent>
  </Card>
);

const MetricBar = ({ label, value, total, color }) => {
  const percentage = total > 0 ? (value / total) * 100 : 0;
  return (
    <div className="space-y-1">
      <div className="flex justify-between text-sm">
        <span className="text-indigo-400">{label}</span>
        <span className="text-slate-400">{value}</span>
      </div>
      <div className="h-2 bg-slate-400 rounded-full overflow-hidden">
        <div
          className={`h-full ${color} transition-all duration-500`}
          style={{ width: `${percentage}%` }}
        />
      </div>
    </div>
  );
};

const DATE_RANGES = [
  { value: "7d", label: "Last 7 days" },
  { value: "30d", label: "Last 30 days" },
  { value: "90d", label: "Last 90 days" },
  { value: "year", label: "This year" }
];

export default function AdvancedAnalytics() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hasAccess, setHasAccess] = useState(true);
  const [dateRange, setDateRange] = useState("30d");

  useEffect(() => {
    fetchAnalytics();
  }, [dateRange]);

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/analytics/advanced?range=${dateRange}`);
      
      if (res.data.feature_gated) {
        setHasAccess(false);
      } else {
        setData(res.data);
        setHasAccess(true);
      }
    } catch (e) {
      if (e.response?.status === 403) {
        setHasAccess(false);
      } else {
        toast.error("Failed to load analytics");
      }
    } finally {
      setLoading(false);
    }
  };

  const exportReport = () => {
    if (!data) return;
    
    const report = {
      generated_at: new Date().toISOString(),
      period: dateRange,
      ...data
    };
    
    const blob = new Blob([JSON.stringify(report, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `advanced-analytics-${dateRange}-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    
    toast.success("Report exported!");
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="advanced-analytics-loading">
        <RefreshCw className="w-8 h-8 text-indigo-400 animate-spin" />
      </div>
    );
  }

  if (!hasAccess) {
    return (
      <div className="space-y-6" data-testid="advanced-analytics-gated">
        <div className="flex items-center gap-3">
          <TrendingUp className="w-7 h-7 text-indigo-400" />
          <div>
            <h1 className="text-2xl font-bold text-indigo-500">Advanced Analytics</h1>
            <p className="text-indigo-400">Deep insights into your business metrics</p>
          </div>
        </div>
        
        <Card className="bg-slate-800/50 border-indigo-500/30">
          <CardContent className="py-12 text-center">
            <Lock className="w-16 h-16 text-indigo-400 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-indigo-500 mb-2">Growth Tier Feature</h2>
            <p className="text-indigo-400 mb-6 max-w-md mx-auto">
              Advanced analytics with revenue tracking, conversion rates, and business trends
              is available on the Growth tier.
            </p>
            <Button 
              onClick={() => window.location.href = "/billing"}
              className="bg-gradient-to-r from-indigo-500 to-teal-500 hover:from-indigo-600 hover:to-slate-400"
            >
              Upgrade to Growth
            </Button>
          </CardContent>
        </Card>
        
        {/* Teaser of what they're missing */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 opacity-50 pointer-events-none">
          <StatCard icon={DollarSign} label="Revenue" value="$---.--" color="bg-green-500/20 text-green-400" />
          <StatCard icon={Users} label="Client Growth" value="--" color="bg-blue-500/20 text-indigo-400" />
          <StatCard icon={FileText} label="Conversion Rate" value="--%" color="bg-indigo-500/20 text-indigo-400" />
          <StatCard icon={Share2} label="Affiliate Earnings" value="$---.--" color="bg-indigo-500/20 text-indigo-400" />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="advanced-analytics">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <TrendingUp className="w-7 h-7 text-indigo-400" />
          <div>
            <h1 className="text-2xl font-bold text-indigo-500">Advanced Analytics</h1>
            <p className="text-indigo-400">Deep insights into your business metrics</p>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          <Select value={dateRange} onValueChange={setDateRange}>
            <SelectTrigger className="bg-slate-800 border-slate-700 text-white">
              <Calendar className="w-4 h-4 mr-2 text-indigo-400" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-slate-800 border-slate-700">
              {DATE_RANGES.map((range) => (
                <SelectItem key={range.value} value={range.value} className="text-indigo-500">
                  {range.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          <Button 
            variant="outline" 
            className="border-slate-600 text-slate-400"
            onClick={exportReport}
          >
            <Download className="w-4 h-4 mr-2" />
            Export
          </Button>
          
          <Button 
            variant="outline" 
            className="border-slate-600 text-slate-400"
            onClick={fetchAnalytics}
          >
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Revenue Overview */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          icon={DollarSign}
          label="Total Revenue"
          value={`$${(data?.revenue?.total || 0).toLocaleString()}`}
          subtext={`Avg: $${(data?.revenue?.average_project || 0).toFixed(0)}/project`}
          color="bg-green-500/20 text-green-400"
        />
        <StatCard
          icon={DollarSign}
          label="Completed Revenue"
          value={`$${(data?.revenue?.completed || 0).toLocaleString()}`}
          color="bg-indigo-500/20 text-indigo-400"
        />
        <StatCard
          icon={DollarSign}
          label="Pending Revenue"
          value={`$${(data?.revenue?.pending || 0).toLocaleString()}`}
          subtext="In active/pending projects"
          color="bg-indigo-500/20 text-indigo-400"
        />
        <StatCard
          icon={Users}
          label="New Clients"
          value={data?.clients?.new_this_period || 0}
          subtext={`${(data?.clients?.acquisition_rate || 0).toFixed(1)}/month rate`}
          trend={data?.clients?.new_this_period > 0 ? "up" : "same"}
          trendValue={`${data?.clients?.total || 0} total`}
          color="bg-blue-500/20 text-indigo-400"
        />
      </div>

      {/* Contracts & Proposals */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Contract Performance */}
        <Card className="bg-slate-800/50 border-slate-700/50">
          <CardHeader>
            <CardTitle className="text-indigo-500 flex items-center gap-2">
              <FileSignature className="w-5 h-5 text-indigo-400" />
              Contract Performance
            </CardTitle>
            <CardDescription className="text-indigo-400">
              Sign rates and pending contracts
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="p-4 bg-slate-900/50 rounded-lg text-center">
                <p className="text-3xl font-bold text-indigo-500">{data?.contracts?.signed || 0}</p>
                <p className="text-sm text-indigo-400">Signed</p>
              </div>
              <div className="p-4 bg-slate-900/50 rounded-lg text-center">
                <p className="text-3xl font-bold text-indigo-400">{data?.contracts?.pending || 0}</p>
                <p className="text-sm text-indigo-400">Pending</p>
              </div>
            </div>
            
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-indigo-400">Sign Rate</span>
                <span className="text-indigo-500 font-medium">{data?.contracts?.sign_rate || 0}%</span>
              </div>
              <Progress value={data?.contracts?.sign_rate || 0} className="h-2" />
            </div>
          </CardContent>
        </Card>

        {/* Proposal Conversion */}
        <Card className="bg-slate-800/50 border-slate-700/50">
          <CardHeader>
            <CardTitle className="text-indigo-500 flex items-center gap-2">
              <FileText className="w-5 h-5 text-indigo-400" />
              Proposal Conversion
            </CardTitle>
            <CardDescription className="text-indigo-400">
              Proposal acceptance metrics
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="p-4 bg-slate-900/50 rounded-lg text-center">
                <p className="text-3xl font-bold text-indigo-500">{data?.proposals?.total || 0}</p>
                <p className="text-sm text-indigo-400">Total Sent</p>
              </div>
              <div className="p-4 bg-slate-900/50 rounded-lg text-center">
                <p className="text-3xl font-bold text-green-400">{data?.proposals?.accepted || 0}</p>
                <p className="text-sm text-indigo-400">Accepted</p>
              </div>
            </div>
            
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-indigo-400">Conversion Rate</span>
                <span className="text-indigo-500 font-medium">{data?.proposals?.conversion_rate || 0}%</span>
              </div>
              <Progress value={data?.proposals?.conversion_rate || 0} className="h-2" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Affiliate Stats */}
      {data?.affiliate && data.affiliate.total_codes > 0 && (
        <Card className="bg-gradient-to-r from-indigo-500/10 to-indigo-500/10 border-indigo-500/30">
          <CardHeader>
            <CardTitle className="text-indigo-500 flex items-center gap-2">
              <Share2 className="w-5 h-5 text-indigo-400" />
              Affiliate Performance
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="p-4 bg-slate-900/30 rounded-lg text-center">
                <p className="text-2xl font-bold text-indigo-500">{data.affiliate.total_codes}</p>
                <p className="text-sm text-indigo-400">Active Codes</p>
              </div>
              <div className="p-4 bg-slate-900/30 rounded-lg text-center">
                <p className="text-2xl font-bold text-indigo-400">{data.affiliate.total_referrals}</p>
                <p className="text-sm text-indigo-400">Total Referrals</p>
              </div>
              <div className="p-4 bg-slate-900/30 rounded-lg text-center">
                <p className="text-2xl font-bold text-green-400">{data.affiliate.total_conversions}</p>
                <p className="text-sm text-indigo-400">Conversions</p>
              </div>
              <div className="p-4 bg-slate-900/30 rounded-lg text-center">
                <p className="text-2xl font-bold text-indigo-400">${(data.affiliate.total_earnings || 0).toFixed(2)}</p>
                <p className="text-sm text-indigo-400">Earnings</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Monthly Trends */}
      {data?.trends?.monthly && data.trends.monthly.length > 0 && (
        <Card className="bg-slate-800/50 border-slate-700/50">
          <CardHeader>
            <CardTitle className="text-indigo-500 flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-indigo-400" />
              Monthly Trends
            </CardTitle>
            <CardDescription className="text-indigo-400">
              Projects and revenue over time
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {/* Simple Bar Chart */}
              <div className="flex items-end justify-between gap-2 h-40">
                {data.trends.monthly.map((month, index) => {
                  const maxRevenue = Math.max(...data.trends.monthly.map(m => m.revenue), 1);
                  const height = (month.revenue / maxRevenue) * 100;
                  return (
                    <div key={index} className="flex-1 flex flex-col items-center gap-2">
                      <div className="w-full relative flex-1 flex items-end">
                        <div 
                          className="w-full bg-gradient-to-t from-indigo-500 to-teal-500 rounded-t-lg transition-all duration-500"
                          style={{ height: `${Math.max(height, 5)}%` }}
                        />
                      </div>
                      <div className="text-center">
                        <p className="text-xs text-indigo-400 truncate w-16">
                          {month.month.split(' ')[0].slice(0, 3)}
                        </p>
                        <p className="text-xs text-indigo-400 font-medium">
                          ${month.revenue.toLocaleString()}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
              
              {/* Summary Table */}
              <div className="border-t border-slate-700 pt-4">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-indigo-400">
                      <th className="text-left pb-2">Month</th>
                      <th className="text-right pb-2">Projects</th>
                      <th className="text-right pb-2">Revenue</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.trends.monthly.map((month, index) => (
                      <tr key={index} className="text-slate-400 border-t border-slate-700">
                        <td className="py-2">{month.month}</td>
                        <td className="text-right">{month.projects}</td>
                        <td className="text-right text-indigo-400">${month.revenue.toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
